import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  TextField,
  Button,
  Chip,
  Grow,
  useTheme,
} from '@mui/material';
import Select from '../../../Components/Inputs/Select';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CheckIcon from '@mui/icons-material/Check';
import { useSetAtom } from 'jotai';
import { culture, addZero } from '../../../Helpers/dates';
import { getEventBaseColor } from '../Utils/constants';
import '../../../Styles/appointments-style.scss';
import {
  additionalCustomerDataAtom,
  selectedCustomerIdAtom,
} from '../../../Atoms';

const dateOptions = {
  weekday: 'long',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const CANCELED_OUTCOME = 17;

const infoRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  color: 'text.secondary',
};

const ShowEventWidget = function ShowEventWidget({
  close,
  setEdit,
  event = null,
  handleEndAppointment = () => {},
  handleOpenActivityDialog = () => {},
  outcomes = [],
}) {
  const intl = useIntl();
  const theme = useTheme();

  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const [completeEvent, setCompleteEvent] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  const toggleCompleteEvent = () => setCompleteEvent((prev) => !prev);

  const isRecall = event?.type === 'Recall';
  const isCanceled = event?.outcome === CANCELED_OUTCOME;
  const isOpen = !event?.outcome || event?.outcome === 100;

  const stringStartTime = addZero(new Date(event?.startDate));
  const stringEndTime = addZero(new Date(event?.endDate));
  const stringDate = new Date(event?.startDate).toLocaleDateString(
    culture,
    dateOptions
  );

  const eventOutcome = [...outcomes]?.find(
    ({ id }) => id === event?.outcome
  )?.name;

  const eventTypeLabel = isRecall
    ? intl.messages.RECALL
    : event?.type === 'Appuntamento'
      ? intl.messages.APPOINTMENT_TYPE
      : event?.type;

  // Header tint reuses the same accent as the calendar chip for consistency.
  const headerTint = event?.type
    ? theme.custom.bgAndColorStyle(
        getEventBaseColor(event.type, theme),
        theme.palette.mode
      )
    : {};

  const statusColor = isCanceled ? 'default' : isOpen ? 'warning' : 'success';
  const statusLabel = eventOutcome
    ? intl.messages[eventOutcome?.toUpperCase()] || eventOutcome
    : isOpen
      ? intl.messages.EVENT_NOW_OPEN
      : intl.messages.COMPLETED;

  const canComplete = !isRecall && !event?.outcome;
  const recallIsActionable = isRecall && isOpen;

  const handleGoToCustomer = () => {
    if (recallIsActionable) {
      close();
      setSelectedCustomerId(event?.entityId);
      setAdditionalCustomerData({
        customerName: event?.fullName,
        parentId: event?.appointmentId,
        recallTime: new Date(event?.startDate),
        outcomesFilter: 'RECALL',
      });
    }
  };

  const handleConfirmCompletion = () => {
    const nextActivity = outcomes.find(({ id }) => id === selectedOutcome)
      ?.extensionData?.nextActType;
    if (nextActivity) {
      handleOpenActivityDialog({
        entityId: event?.entityId,
        outcome: selectedOutcome,
        notes: notesValue,
        nextActivity,
      });
    } else {
      handleEndAppointment({
        entityId: event?.entityId,
        outcome: selectedOutcome,
        notes: notesValue,
        campaignId: event?.commercialCampaignId,
      });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Accent header */}
      <Box
        sx={{
          ...headerTint,
          p: 2.5,
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Chip
            size="small"
            icon={
              isRecall ? (
                <ContactPhoneOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <BusinessIcon sx={{ fontSize: 16 }} />
              )
            }
            label={eventTypeLabel}
            sx={{
              bgcolor: 'rgba(255,255,255,0.35)',
              color: 'inherit',
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={intl.messages.EDIT} placement="bottom">
              <IconButton size="small" color="inherit" onClick={() => setEdit(true)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canComplete && (
              <Tooltip title={intl.messages.COMPLETE} placement="bottom">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={toggleCompleteEvent}
                >
                  <AssignmentTurnedInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={intl.messages.CLOSE} placement="bottom">
              <IconButton size="small" color="inherit" onClick={close}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          variant="h5"
          title={event?.title}
          onClick={handleGoToCustomer}
          className={recallIsActionable ? 'to-customer-link' : undefined}
          sx={{
            fontWeight: 600,
            lineHeight: 1.2,
            textDecoration: isCanceled ? 'line-through' : 'none',
            cursor: recallIsActionable ? 'pointer' : 'default',
          }}
        >
          {event?.title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.85 }}>
          <ScheduleRoundedIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            {`${stringDate} · ${stringStartTime} – ${stringEndTime}`}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Chip
          size="small"
          variant={statusColor === 'default' ? 'filled' : 'outlined'}
          color={statusColor}
          icon={
            isCanceled ? undefined : isOpen ? (
              <PendingActionsIcon />
            ) : (
              <TaskAltIcon />
            )
          }
          label={statusLabel}
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />

        <Box sx={infoRowSx}>
          <PersonRoundedIcon fontSize="small" />
          <Typography variant="body2">
            {`${intl.messages.CREATED_BY} ${event?.createdByFullName}`}
          </Typography>
        </Box>

        {event?.remindBefore && (
          <Box sx={infoRowSx}>
            <NotificationsRoundedIcon fontSize="small" />
            <Typography variant="body2">
              {`${event.remindBefore} ${intl.messages.MINUTES_BEFORE}`}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <NotesRoundedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 1 }} />
          {!completeEvent ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 2,
                bgcolor: 'action.hover',
                color: 'text.primary',
                borderRadius: 2,
                maxHeight: '22vh',
                overflow: 'auto',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {event?.desc || intl.messages.UNAVAILABLE}
              </Typography>
            </Box>
          ) : (
            <TextField
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              id="event-notes"
              label={intl.messages.COMPLETION_NOTES}
              variant="filled"
              fullWidth
              multiline
              rows={5}
            />
          )}
        </Box>

        {completeEvent && (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Select
                name="outcome"
                label="OUTCOME"
                options={outcomes.map(({ id, name }) => ({
                  value: id,
                  label: intl.messages[name?.toUpperCase()] || name,
                }))}
                required
                onChange={setSelectedOutcome}
                value={selectedOutcome}
              />
            </Box>
            <Grow in>
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                disabled={!selectedOutcome}
                onClick={handleConfirmCompletion}
                sx={{ textTransform: 'none', boxShadow: 'none' }}
              >
                {intl.messages.CONFIRM}
              </Button>
            </Grow>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ShowEventWidget;
